import { mapSpecialOfferViewModel } from 'components/StoreLayout/utils';
import React, { useEffect, useRef, useState } from 'react';
import { useStoreState } from 'state/hooks/store.state.hook';
import {
  ESpecialOfferInternalViewModel,
  EBundlesInternalViewModel,
  ESaleDiscountDisplayType,
} from 'utils/enums';
import { formattingCoundown } from 'utils/index';
import { OfferData } from 'utils/types';

const TIME_LEFT_INTERVAL = 1000;
type TimerState = { [key: string]: string };

interface ISpecialOfferProps {
  selectOffer: () => void;
  data: OfferData;
  tooltipOpenEventHandler?: () => void;
  refetchOffers: () => void;
  t: any;
}

export const SpecialOffer: React.FC<ISpecialOfferProps> = ({
  data,
  selectOffer,
  tooltipOpenEventHandler,
  refetchOffers,
  t,
}) => {
  const { storeTheme } = useStoreState();
  const [shouldHideOffer, setShouldHideOffer] = useState<{
    [key: string]: boolean;
  }>({});

  const borderColor = storeTheme!.storeScreen.bundleBorderColor;
  const SpecialOfferDesign =
    mapSpecialOfferViewModel[
      storeTheme?.general?.specialOffersInternalViewModel ||
        ESpecialOfferInternalViewModel.HUGE
    ];
  const specialOfferRef = useRef<HTMLDivElement>(null);
  const [timers, setTimers] = useState<TimerState>({});
  const [timeLeft, setTimeLeft] = useState<{ [key: string]: number | null }>(
    {},
  );

  // Format the timeLeft into HH:MM:SS format based on offerId
  useEffect(() => {
    const offerTimeLeft = timeLeft[data.offerId];
    if (!offerTimeLeft || offerTimeLeft < 0) {
      setTimers((prev) => ({ ...prev, [data.offerId]: '' }));
    } else {
      const formattedCountdown = formattingCoundown(offerTimeLeft);
      setTimers((prev) => ({
        ...prev,
        [data.offerId]: formattedCountdown,
      }));
    }
  }, [timeLeft[data.offerId]]);

  // Effect to trigger the timer calculation when playerAvailability, endsIn or schedule changes
  useEffect(() => {
    const offerId = data.offerId;
    let chosenEndTime: number | null = null;

    const rawEndsIn = data.endsIn;

    const normalizeTimestamp = (ts: number): number =>
      ts < 1e12 ? ts * 1000 : ts;

    let endsInMs: number | null = null;

    if (rawEndsIn) {
      if (rawEndsIn < 1e10) {
        endsInMs = Date.now() + rawEndsIn * 1000;
      } else {
        endsInMs = normalizeTimestamp(rawEndsIn);
      }
    }

    if (endsInMs && endsInMs > Date.now()) {
      chosenEndTime = endsInMs;
    }

    if (!chosenEndTime && data.schedule && !data.schedule.permanent) {
      const scheduleEnd = data.schedule.timeFrames?.[0]?.endTime;
      if (scheduleEnd) {
        const scheduleEndMs = new Date(scheduleEnd).getTime();

        if (scheduleEndMs > Date.now()) {
          chosenEndTime = scheduleEndMs;
        }
      }
    }

    if (chosenEndTime) {
      const clearTimer = calculateTimeLeft(chosenEndTime, offerId);
      return clearTimer;
    } else {
      setTimers((prev) => ({ ...prev, [offerId]: '' }));
      setTimeLeft((prev) => ({ ...prev, [offerId]: null }));
    }
  }, [data]);

  // Calculate time left until the endTime
  const calculateTimeLeft = (
    endTime: number | string | Date,
    offerId: string,
  ) => {
    const interval = setInterval(() => {
      const currentTime = Date.now();
      const endTimeMs =
        typeof endTime === 'number' ? endTime : new Date(endTime).getTime();

      const remainingTime = endTimeMs - currentTime;

      setTimeLeft((prev) => ({
        ...prev,
        [offerId]: remainingTime > 0 ? remainingTime : null,
      }));
      if (remainingTime <= 0) {
        clearInterval(interval);
        setTimers((prev) => ({ ...prev, [offerId]: '' }));
        setShouldHideOffer((prev) => ({ ...prev, [offerId]: true }));
        refetchOffers();
      }
    }, TIME_LEFT_INTERVAL);

    return () => clearInterval(interval);
  };

  return (
    <>
      {shouldHideOffer[data.offerId] ? null : (
        <SpecialOfferDesign
          availability={data.playerAvailability}
          dataTestId={data.offerId}
          playerAvailability={data.offerPlayerRemainingAvailability}
          backgroundColor={data.offerUi.specialOffer!.backgroundColor}
          title={data.offerUi.specialOffer!.title}
          textColor={data.offerUi.specialOffer!.fontColor}
          fontSize={data.offerUi.specialOffer!.fontSize}
          fontWeight={data.offerUi.specialOffer!.fontWeight}
          subTitle={data.offerUi.specialOffer!.subTitle?.text}
          subTitleColor={data.offerUi.specialOffer!.subTitle?.fontColor}
          subTitleSize={data.offerUi.specialOffer!.subTitle?.fontSize}
          subTitleWeight={data.offerUi.specialOffer!.subTitle?.fontWeight}
          borderColor={borderColor}
          buttonColor={storeTheme!.storeScreen.buttonColor}
          backgroundImage={data.offerUi.backgroundImage}
          design={
            storeTheme?.general.bundlesInternalViewModel ===
            EBundlesInternalViewModel.MATCH
              ? EBundlesInternalViewModel.MATCH
              : storeTheme!.general.bundlesInternalViewModel ===
                EBundlesInternalViewModel.CANDIVORE
              ? EBundlesInternalViewModel.CANDIVORE
              : storeTheme!.general.specialOffersInternalViewModel
          }
          prices={data.productsSequence[0]?.prices}
          productsList={data.productsSequence[0].products}
          selectOffer={selectOffer}
          badges={data?.badges} // TODO remove old badge schema
          emblemBadges={data?.offerBadges?.emblems}
          ribbonBadges={data?.offerBadges?.ribbons}
          salePercentage={data.productSale?.sale}
          salePercentageDisplayType={
            data.productSale?.type || ('percentage' as ESaleDiscountDisplayType)
          }
          offerDescription={
            data?.offerDescriptionOverride || data?.description || ''
          }
          isFree={Boolean(!data.productsSequence[0]?.prices?.priceInUsdCents)}
          availabilityText={t ? t('availability') : 'Available'}
          collectText={data.offerUi.specialOffer?.collectText || 'Collect'}
          specialOfferRef={specialOfferRef}
          timer={timers[data.offerId] || ''}
          translations={{
            includes: t ? t('bundles.subProductsPrefixText') : 'Includes',
          }}
          tooltipOpenEventHandler={tooltipOpenEventHandler}
          offerName={data.offerName}
          offerDisplayName={data?.displayName || ''}
          buttonSuffixImage={data.offerUi?.buttonSuffixImage || ''}
          buttonSuffixAnimation={data.offerUi?.buttonSuffixAnimation || ''}
        />
      )}
    </>
  );
};
